import axios from 'axios';
import { clearAuthStorage, getStoredToken } from '../utils/auth';


const API_BASE_URL = process.env.REACT_APP_API_URL;

const PUBLIC_AUTH_ENDPOINTS = [
    '/auth/login',
    '/auth/forgot-password',
    '/auth/reset-password'
];

const shouldSkipAuthRedirect = (config = {}) => Boolean(config.skipAuthRedirect);

const isPublicAuthRequest = (url = '') => PUBLIC_AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

const dispatchWindowEvent = (eventName, detail = {}) => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(eventName, { detail }));
    }
};

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add token to requests if available
api.interceptors.request.use(
    (config) => {
        const token = getStoredToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status;
        const code = error.response?.data?.code;
        const requestUrl = error.config?.url || '';

        if (code === 'PASSWORD_CHANGE_REQUIRED') {
            dispatchWindowEvent('auth:password-change-required');
            return Promise.reject(error);
        }
        if (
            !shouldSkipAuthRedirect(error.config) &&
            getStoredToken() &&
            status === 401 &&
            !isPublicAuthRequest(requestUrl)
        ) {
            clearAuthStorage();
            dispatchWindowEvent('auth:logout-required', {
                message: error.response?.data?.message || 'Session expired. Please login again.'
            });
        }

        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    signup: (email, password) => api.post('/auth/signup', { email, password }),
    login: (email, password) => api.post('/auth/login', { email, password }),
    validateSession: () => api.get('/auth/session', { skipAuthRedirect: true }),
    logout: () => api.post('/auth/logout', {}, { skipAuthRedirect: true }),
    changePassword: (currentPassword, newPassword, confirmPassword) => api.post('/auth/change-password', {
        currentPassword,
        newPassword,
        confirmPassword
    })
};

// Dashboard API
export const dashboardAPI = {
    getDashboard: () => api.get('/dashboard')
};

export const youtubeAPI = {
    getVideos: (refresh = false, productId = '') => api.get('/youtube/videos', {
        params: { refresh, ...(productId ? { productId } : {}) }
    }),
    syncVideos: (force = false, productId = '') => api.post('/youtube/sync', {
        force,
        ...(productId ? { productId } : {})
    }),
    getOAuthUrl: (userId) => api.get(`/youtube/oauth/url/${userId}`),
    getProductOAuthUrl: (productId) => api.get(`/youtube/oauth/product/${productId}`),
    sendProductYoutubeInvite: (productId) => api.get(`/youtube/oauth/product/${productId}`, { params: { sendEmail: 'true' } }),
    getRevenue: (userId, params = {}) => api.get(`/youtube/revenue/${userId}`, { params }),
    syncRevenue: (userId = null, productId = null, startDate = null, endDate = null) => api.post('/youtube/revenue/sync', {}, {
        params: {
            ...(userId ? { userId } : {}),
            ...(productId ? { productId } : {}),
            ...(startDate ? { startDate } : {}),
            ...(endDate ? { endDate } : {})
        }
    }),
    getTrafficSources: (params = {}) => api.get('/youtube/traffic-sources', { params }),
    // Per-video analytics: views/revenue over time, traffic sources, demographics
    getVideoAnalytics: (id, params = {}) => api.get(`/youtube/videos/${id}/analytics`, { params }),
    // Raw per-video subscribers gained for the requested date range (no normalisation)
    getSubscribersGainedPerVideo: (params = {}) => api.get('/youtube/subscribers-gained', { params }),
    // Admin-only: update editable fields of a single video
    updateVideo: (id, payload) => api.patch(`/youtube/videos/${id}`, payload),
    // Admin-only: get OAuth token status for all connected YouTube channels
    getOAuthStatus: () => api.get('/youtube/oauth/status')
};

export const adminAPI = {
    getUsers: () => api.get('/admin/users'),
    createUser: (payload) => api.post('/admin/users', payload),
    updateUser: (userId, payload) => api.patch(`/admin/users/${userId}`, payload),
    updateUserStatus: (userId, isActive) => api.patch(`/admin/users/${userId}/status`, { isActive }),
    resetUserPassword: (userId) => api.post(`/admin/users/${userId}/reset-password`),
    deleteUser: (userId) => api.delete(`/admin/users/${userId}`)
};

export const currencyAPI = {
    getLatestRate: () => api.get('/currency/latest-rate')
};

export const metaAPI = {
    getInsights: (params = {}) => api.get('/meta/insights', { params }),
    syncNow: (body = {}) => api.post('/meta/sync', body),
    getTokenStatus: () => api.get('/meta/token-status'),
    exportData: (params = {}) => api.get('/meta/export', { params }),
};

export const googleAPI = {
    getInsights: (params = {}) => api.get('/google/insights', { params }),
    syncNow: (body = {}) => api.post('/google/sync', body),
    getCredentialStatus: () => api.get('/google/credential-status'),
    getOAuthUrl: () => api.get('/google/oauth/url'),

    sendGoogleAdsEmail: () => api.post('/google/oauth/send-email'),
};


export const playConsoleAPI = {
    getUninstalls: ({ since, until, forceRefresh = false } = {}) =>
        api.get('/play-console/uninstalls', {
            params: {
                ...(since ? { since } : {}),
                ...(until ? { until } : {}),
                ...(forceRefresh ? { refresh: 'true' } : {}),
            },
        }),
};

export const playStoreAPI = {
    getMonthStats: ({ month, forceRefresh = false } = {}) =>
        api.get('/playstore/installs', {
            params: {
                ...(month ? { month } : {}),
                ...(forceRefresh ? { refresh: 'true' } : {}),
            },
        }),
    getRangeStats: ({ since, until, forceRefresh = false } = {}) =>
        api.get('/playstore/installs/range', {
            params: {
                ...(since ? { since } : {}),
                ...(until ? { until } : {}),
                ...(forceRefresh ? { refresh: 'true' } : {}),
            },
        }),
    getIosRangeStats: ({ since, until, forceRefresh = false } = {}) =>
        api.get('/playstore/installs/ios/range', {
            params: {
                ...(since ? { since } : {}),
                ...(until ? { until } : {}),
                ...(forceRefresh ? { refresh: 'true' } : {}),
            },
        }),
    getIosDailyBreakdown: ({ month } = {}) =>
        api.get('/playstore/installs/ios/daily', {
            params: { ...(month ? { month } : {}) },
        }),
    getYesterday: () =>
        api.get('/playstore/installs/yesterday'),
    syncMonth: ({ month } = {}) =>
        api.post('/playstore/installs/sync', { ...(month ? { month } : {}) }),
    syncIosMonth: ({ month } = {}) =>
        api.post('/playstore/installs/ios/sync', { ...(month ? { month } : {}) }),
    getSyncStatus: () =>
        api.get('/playstore/installs/sync-status'),
};

export const productsAPI = {
    list: (params = {}) => api.get('/products', { params }),
    create: (payload) => api.post('/products', payload),
    update: (productId, payload) => api.patch(`/products/${productId}`, payload),
    remove: (productId) => api.delete(`/products/${productId}`),
    permanentDelete: (productId) => api.delete(`/products/${productId}/permanent`),
    addCredential: (productId, payload) => api.post(`/products/${productId}/credentials`, payload),
    removeCredential: (productId, credentialId) => api.delete(`/products/${productId}/credentials/${credentialId}`),
    getDashboard: (productId) => api.get(`/products/${productId}/dashboard`)
};

export default api;
